import { Dictionary } from '../@types';
import IReportingEvent from '../interfaces/IReportingEvent';
import {ITrackingServerConfig, IReportingParamNameMap } from '../interfaces/ITrackingServerConfig';
import AxiosAdapter from './AxiosAdapter';

export default class Reporter {
    config: ITrackingServerConfig;

    constructor(config: ITrackingServerConfig) {
        config.baseParams = (config.baseParams) ? config.baseParams : {};
        config.baseParams.iid = (config.baseParams.iid) ? config.baseParams.iid : Date.now();

        this.config = config;
    }

    protected async sendThirdPartyReport(type: string): Promise<void> {
        try {
            if (!this.config.thirdPartyUrls || !this.config.thirdPartyUrls[type]) {
                return;
            }

            this.config.thirdPartyUrls[type].forEach((url) => { AxiosAdapter.get(url); });
        } catch (err) {
            console.warn(`WARNING: error received when ending third party report for ${type}, err = `, err);
        }
    }

    protected getUrlWithQueryStringParams(event: IReportingEvent): string {
        if (!this.config.baseParams) {
            return `${this.config.url}${this.objectToQueryString(event)}`;
        }
        return `${this.config.url}${this.objectToQueryString(this.config.baseParams)}&${this.objectToQueryString(event)}`;
    }

    protected getMappedParams(event: IReportingEvent): IReportingEvent {
        if (!this.config.paramNameMap) {
            return event;
        }
        const mapConfig: IReportingParamNameMap | undefined = this.config.paramNameMap.find((pnm: IReportingParamNameMap) => pnm.type === event.type);

        if (!mapConfig) {
            return event;
        }

        const mappedParams: Dictionary<string> = {};
        const keys = Object.keys(mapConfig.paramMap);
        const { length } = keys;
        let key;

        for (let i = 0; i < length; i++) {
            key = keys[i];
            if (Object.prototype.hasOwnProperty.call(event, key)) {
                mapConfig.paramMap[key] = event[key] as string;
                // eslint-disable-next-line no-param-reassign
                delete event[key];
            }
        }

        return {
            ...event,
            ...mappedParams,
            ...mapConfig.staticParams,
        };
    }

    // TODO: move this to a globally available utility
    /**
     * `objectToQueryString` converts and object into key/value query string formatted params
     * @param source: object - the params object to convert
     * @param parentKey: string - when nested child, the key of the parent object (for LHS brackets);
     * @return string
     */
    private objectToQueryString(source: Dictionary<string | number | boolean | Array<string | number | boolean> | object>, parentKey?: string): string {
        const keys = Object.keys(source);
        const paramArray: string[] = [];

        for (let i = 0, l = keys.length; i < l; i++) {
            paramArray.push(this.getQueryParam(keys[i], source, parentKey));
        }
        return paramArray.join('&');
    }

    private getQueryParam(key: string, source: Dictionary<string | number | boolean | Array<string | number | boolean> | object>, parentKey?: string): string {
        const returnKey = (!parentKey) ? key : `${parentKey}[${key}]`;
        if (Array.isArray(source[key])) {
            const arr: Array<string | number | boolean> = source[key] as Array<string | number | boolean>;
            return arr.map((val: any) => `${returnKey}[]=${val}`).join('&');
        }

        // https://www.moesif.com/blog/technical/api-design/REST-API-Design-Filtering-Sorting-and-Pagination/#lhs-brackets
        if (typeof (source[key]) === 'object') {
            const obj = source[key] as Dictionary<string | number | boolean | Array<string | number | boolean> | object>;
            return this.objectToQueryString(obj, key);
        }
        return `${returnKey}=${source[key].toString()}`;
    }
}
